import { formatCurrency } from './cateringOrderAmount';

export interface InstallmentScheduleInstallmentLike {
  id?: number;
  installmentNumber?: number;
  name?: string;
  value?: number | string;
  amount?: number;
  percentage?: number;
  originalPercentage?: number;
}

export interface InstallmentScheduleInvoiceLike {
  id?: number;
  installmentId?: number | null;
  amount?: number | null;
  calculatedAmount?: number | null;
  paidAmount?: number | null;
  dueDate?: Date | string | null;
  invoiceStatus?: string;
  ctrInvoicePayment?: {
    paymentStatus?: string;
  } | null;
}

export interface AdjustedInstallmentScheduleItem {
  installmentId?: number;
  installmentNumber: number;
  name: string;
  amount: number;
  percentage: number;
  originalPercentage: number;
  paidAmount: number;
  dueDate: Date | string | null;
  invoiceId?: number;
  invoiceStatus?: string;
  invoice?: InstallmentScheduleInvoiceLike;
}

export interface AdjustedInstallmentScheduleInputItem {
  installmentId?: number;
  installmentNumber?: number;
  name?: string;
  amount?: number;
  percentage?: number;
  originalPercentage?: number;
  paidAmount?: number;
  dueDate?: Date | string | null;
  invoiceId?: number;
  invoiceStatus?: string;
  invoice?: InstallmentScheduleInvoiceLike;
}

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const resolveInvoiceAmount = (invoice?: InstallmentScheduleInvoiceLike): number => {
  if (!invoice) return 0;

  const calculatedAmount = toNumber(invoice.calculatedAmount);
  if (calculatedAmount > 0) {
    return formatCurrency(calculatedAmount);
  }

  return formatCurrency(invoice.amount ?? 0);
};

export const buildAdjustedInstallmentSchedule = ({
  totalAmount,
  installments = [],
  invoices = [],
  paymentPlanInstallments = [],
}: {
  totalAmount: number;
  installments?: InstallmentScheduleInstallmentLike[] | null;
  invoices?: InstallmentScheduleInvoiceLike[] | null;
  paymentPlanInstallments?: AdjustedInstallmentScheduleInputItem[] | null;
}): AdjustedInstallmentScheduleItem[] => {
  if ((paymentPlanInstallments ?? []).length > 0) {
    return [...(paymentPlanInstallments ?? [])]
      .sort((a, b) => (a.installmentNumber ?? 0) - (b.installmentNumber ?? 0))
      .map((installment) => ({
        installmentId: installment.installmentId,
        installmentNumber: installment.installmentNumber ?? 0,
        name: installment.name || `Installment ${installment.installmentNumber ?? 0}`,
        amount: formatCurrency(installment.amount ?? 0),
        percentage: formatCurrency(installment.percentage ?? 0),
        originalPercentage: formatCurrency(installment.originalPercentage ?? installment.percentage ?? 0),
        paidAmount: formatCurrency(installment.paidAmount ?? 0),
        dueDate: installment.dueDate ?? null,
        invoiceId: installment.invoiceId,
        invoiceStatus: installment.invoiceStatus,
        invoice: installment.invoice,
      }));
  }

  const sortedInstallments = [...(installments ?? [])].sort(
    (a, b) => (a.installmentNumber ?? 0) - (b.installmentNumber ?? 0)
  );

  if (!sortedInstallments.length) {
    return [];
  }

  const safeTotalAmount = formatCurrency(totalAmount);
  const lastInstallmentId = sortedInstallments[sortedInstallments.length - 1]?.id;

  let allocatedAmountBeforeLast = 0;

  return sortedInstallments.map((installment) => {
    const invoice = (invoices ?? []).find((entry) => entry.installmentId === installment.id);
    const isLastInstallment = installment.id === lastInstallmentId;
    const originalPercentage = formatCurrency(
      installment.originalPercentage ?? installment.percentage ?? toNumber(installment.value)
    );
    let amount = 0;

    if (typeof installment.amount === 'number' && installment.amount > 0) {
      amount = formatCurrency(installment.amount);
      if (!isLastInstallment) {
        allocatedAmountBeforeLast = formatCurrency(allocatedAmountBeforeLast + amount);
      }
    } else if (isLastInstallment) {
      amount = formatCurrency(Math.max(safeTotalAmount - allocatedAmountBeforeLast, 0));
    } else if (invoice) {
      amount = resolveInvoiceAmount(invoice);
      allocatedAmountBeforeLast = formatCurrency(allocatedAmountBeforeLast + amount);
    } else {
      amount = formatCurrency((originalPercentage / 100) * safeTotalAmount);
      allocatedAmountBeforeLast = formatCurrency(allocatedAmountBeforeLast + amount);
    }

    return {
      installmentId: installment.id,
      installmentNumber: installment.installmentNumber ?? 0,
      name: installment.name || `Installment ${installment.installmentNumber ?? 0}`,
      amount,
      percentage: safeTotalAmount > 0 ? formatCurrency((amount / safeTotalAmount) * 100) : 0,
      originalPercentage,
      paidAmount: formatCurrency(invoice?.paidAmount ?? 0),
      dueDate: invoice?.dueDate ?? null,
      invoiceId: invoice?.id,
      invoiceStatus: invoice?.invoiceStatus,
      invoice,
    };
  });
};
